import * as React from 'react'
import { History } from 'history'
import { RouteComponentProps } from 'react-router'
import { useState, useEffect, useCallback } from 'react'
import {
  PhotoIcon as PhotoLibraryOutlined,
  RectangleStackIcon as PhotoAlbumOutlined,
  ArrowUpTrayIcon as UploadIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import { UserAvatar } from '../Common/UserAvatar'
import { USER_STORE, WORKSPACES_STORE } from '../../stores'
import { UserStore } from '../../stores/UserModel'
import { inject, observer } from 'mobx-react'
import NavBarBase from './NavBarBase/NavBarBase'
import { WorkspacesStore } from '../../stores/WorkspaceStore'
import { UserProfileDrawer } from '../UserProfileDrawer'
import { LogoutButton } from '../LogoutButton'
import { LogoutConfirmModal } from '../LogoutConfirmModal'
import { WorkspaceItem } from '../WorkspaceItem'
import { DrawerHeader } from '../DrawerHeader'

interface NavBarProps extends RouteComponentProps {
  history: History
  [USER_STORE]: UserStore
  [WORKSPACES_STORE]: WorkspacesStore
}

const NavBar: React.FC<NavBarProps> = observer((props) => {
  const [drawerOpened, setDrawerOpened] = useState<boolean>(false)
  const [workspaceDrawerOpened, setWorkspaceDrawerOpened] = useState<boolean>(false)
  const [profileDrawerOpened, setProfileDrawerOpened] = useState<boolean>(false)

  const { history, [WORKSPACES_STORE]: workspacesStore } = props

  const user = props[USER_STORE]
  const initials = (user.first_name.substring(0, 1) + user.last_name.substring(0, 1)).toUpperCase()

  // Close drawer on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpened(false)
        setWorkspaceDrawerOpened(false)
        setProfileDrawerOpened(false)
      }
    }
    const anyDrawerOpen = drawerOpened || workspaceDrawerOpened || profileDrawerOpened
    if (anyDrawerOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [drawerOpened, workspaceDrawerOpened, profileDrawerOpened])

  const closeDrawer = useCallback(() => setDrawerOpened(false), [])
  const closeWorkspaceDrawer = useCallback(() => setWorkspaceDrawerOpened(false), [])
  const closeProfileDrawer = useCallback(() => setProfileDrawerOpened(false), [])

  const navigateTo = useCallback((path: string) => {
    history.push(path)
    setDrawerOpened(false)
    setWorkspaceDrawerOpened(false)
    setProfileDrawerOpened(false)
  }, [history])

  const selectWorkspace = useCallback((workspaceId: string) => {
    const workspace = workspacesStore.getWorkspaceById(workspaceId)
    if (workspace) {
      workspacesStore.setActiveWorkspace(workspace)
      navigateTo(`/${workspace.slug}`)
    }
  }, [workspacesStore, navigateTo])

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState<boolean>(false)

  const handleLogout = useCallback(() => {
    setLogoutConfirmOpen(true)
  }, [])

  const handleConfirmLogout = useCallback(() => {
    setLogoutConfirmOpen(false)
    setProfileDrawerOpened(false)
    user.logout()
    history.push('/login')
  }, [user, history])

  return (
    <div className="w-full fixed top-0 z-50 bg-white border-b border-gray-200">
      <NavBarBase
        {...props}
        workspaceSlug={workspacesStore.activeWorkspaceSlug || ''}
        activeWorkspace={workspacesStore.activeWorkspace}
        toggleDrawer={setDrawerOpened}
        toggleWorkspaceDrawer={setWorkspaceDrawerOpened}
        onProfileClick={() => setProfileDrawerOpened(true)}
        initials={initials}
      />

      {/* Drawer backdrop */}
      <div
        className={`fixed inset-0 bg-gray-500/75 transition-opacity z-40 ${
          drawerOpened ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className={`fixed inset-y-0 right-0 z-50 flex transform transition-transform duration-300 ease-in-out ${
          drawerOpened ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Mobile: full width, Desktop: fixed width */}
        <div className="relative w-screen sm:max-w-md">
          <div className="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
            <DrawerHeader onClose={closeDrawer} />

            {/* Content */}
            <div className="flex-1 px-4 py-3 sm:px-6">
              {/* User profile section */}
              <button
                onClick={() => setProfileDrawerOpened(true)}
                className="flex w-full items-center gap-x-3 rounded-lg px-2 py-2 hover:bg-gray-50 transition-colors"
              >
                <UserAvatar
                  size="md"
                  initials={initials}
                  imageUrl={user.avatar}
                  color={user.color}
                />
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs text-gray-500">Zobacz profil</p>
                </div>
              </button>

              <div className="mt-4 border-t border-gray-200 pt-4">
                <nav className="space-y-1">
                  <button
                    onClick={() => navigateTo(`/${workspacesStore.activeWorkspaceSlug}`)}
                    className="flex w-full items-center gap-x-3 rounded-lg px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    <PhotoLibraryOutlined className="h-6 w-6 text-gray-400" />
                    Moje zdjęcia
                  </button>
                  <button
                    onClick={() => navigateTo(`/${workspacesStore.activeWorkspaceSlug}/albums`)}
                    className="flex w-full items-center gap-x-3 rounded-lg px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    <PhotoAlbumOutlined className="h-6 w-6 text-gray-400" />
                    Albumy
                  </button>
                  <button
                    onClick={() => navigateTo(`/${workspacesStore.activeWorkspaceSlug}/timelines`)}
                    className="flex w-full items-center gap-x-3 rounded-lg px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    <ClockIcon className="h-6 w-6 text-gray-400" />
                    Osi czasu
                  </button>
                  <button
                    onClick={() => navigateTo(`/${workspacesStore.activeWorkspaceSlug}/upload`)}
                    className="flex w-full items-center gap-x-3 rounded-lg px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    <UploadIcon className="h-6 w-6 text-gray-400" />
                    Kolejka przesyłania
                  </button>
                </nav>
              </div>

              {/* Workspaces section */}
              <div className="mt-6 border-t border-gray-200 pt-6">
                <h3 className="px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Przestrzenie
                </h3>
                <div className="mt-3 space-y-1">
                  {workspacesStore.workspaces.map((workspace) => (
                    <WorkspaceItem
                      key={workspace.id}
                      workspace={workspace}
                      isActive={workspace.id === workspacesStore.activeWorkspaceId}
                      showSlug={true}
                      showCheckmark={true}
                      onClick={() => selectWorkspace(workspace.id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Workspace Drawer backdrop */}
      <div
        className={`fixed inset-0 bg-gray-500/75 transition-opacity z-40 ${
          workspaceDrawerOpened ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeWorkspaceDrawer}
        aria-hidden="true"
      />

      {/* Workspace Drawer panel - Desktop only, narrower */}
      <div
        className={`fixed inset-y-0 right-0 z-50 flex transform transition-transform duration-300 ease-in-out ${
          workspaceDrawerOpened ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="relative w-80">
          <div className="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
            <DrawerHeader title="Przestrzenie" onClose={closeWorkspaceDrawer} />

            {/* Content - Workspaces list only */}
            <div className="flex-1 px-4 py-4">
              <div className="space-y-1">
                {workspacesStore.workspaces.map((workspace) => (
                  <WorkspaceItem
                    key={workspace.id}
                    workspace={workspace}
                    isActive={workspace.id === workspacesStore.activeWorkspaceId}
                    showSlug={true}
                    showCheckmark={true}
                    onClick={() => selectWorkspace(workspace.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Profile Drawer */}
      <UserProfileDrawer
        isOpen={profileDrawerOpened}
        onClose={closeProfileDrawer}
        user={user}
        isCurrentUser={true}
        onViewProfile={() => navigateTo(`/${workspacesStore.activeWorkspaceSlug}/profile`)}
        actions={<LogoutButton onClick={handleLogout} />}
      />

      <LogoutConfirmModal
        isOpen={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        onConfirm={handleConfirmLogout}
      />
    </div>
  )
})

export default inject(WORKSPACES_STORE)(inject(USER_STORE)(NavBar))
